import { useLazyQuery, useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Issue } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  CANCEL_WASTE_SHIPMENT_MUTATION,
  DELETE_WASTE_SHIPMENT_MUTATION,
  SAVE_WASTE_SHIPMENT_MUTATION,
  VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { isShipmentLevelDiff } from "./reconciliationDisplay";
import { EditShipmentFormValues } from "./ShipmentDetailsCard";
import { REFETCH, Shipment } from "./shipmentDetailTypes";
import { storedDateToDateOnly } from "./shipmentDisplay";
import { useStagedShipmentLines } from "./useStagedShipmentLines";

/**
 * The manifest comparison, split by the grain it belongs to.
 *
 * The discriminator for a shipment-level row is the FIELD, not a null
 * `wasteStreamId` — null is also the identity of the uncharacterized bucket, so
 * waste with no stream assigned produces real quantity and code rows that
 * happen to carry a null id.
 *
 * One memo over the whole sort, because `diffsByStream` is handed to the
 * contents grid and its identity is what the grid's column memo keys on.
 * Rebuilt every render, it made that memo — and the grouping column definition
 * that depends on it — recompute on every keystroke in the Details fields, for
 * exactly the manifest-attached shipments where the grid is heaviest and also
 * carries the Discrepancies column.
 */
export function useReconciliationDiffs(shipment: Shipment) {
  return useMemo(() => {
    const streamIds = new Set(
      shipment.lines.map((line) => line.wasteStream?.id).filter((id): id is string => !!id)
    );
    const hasUncharacterizedLines = shipment.lines.some((line) => !line.wasteStream);
    const byStream = new Map<string, typeof shipment.reconciliation.fieldDiffs>();
    const manifestOnly: typeof shipment.reconciliation.fieldDiffs = [];
    const shipmentLevel: typeof shipment.reconciliation.fieldDiffs = [];
    const uncharacterized: typeof shipment.reconciliation.fieldDiffs = [];
    for (const diff of shipment.reconciliation.fieldDiffs) {
      if (isShipmentLevelDiff(diff.field)) {
        shipmentLevel.push(diff);
      } else if (!diff.wasteStreamId) {
        // Uncharacterized waste on one side or both. It belongs with the
        // contents when this shipment actually carried some, and reads as
        // manifest-only when it did not.
        if (hasUncharacterizedLines) {
          uncharacterized.push(diff);
        } else {
          manifestOnly.push(diff);
        }
      } else if (streamIds.has(diff.wasteStreamId)) {
        byStream.set(diff.wasteStreamId, [...(byStream.get(diff.wasteStreamId) ?? []), diff]);
      } else {
        manifestOnly.push(diff);
      }
    }
    return {
      diffsByStream: byStream,
      manifestOnlyDiffs: manifestOnly,
      shipmentLevelDiffs: shipmentLevel,
      uncharacterizedDiffs: uncharacterized,
    };
  }, [shipment.lines, shipment.reconciliation.fieldDiffs]);
}

/**
 * Saving the page's pending edits.
 *
 * One mutation, one transaction: sending the header, container and pickup
 * changes separately meant a rule that rejected the second could leave the
 * first committed while the user was told the save failed.
 */
export function useSaveShipment({
  shipment,
  form,
  staged,
}: {
  shipment: Shipment;
  form: ReturnType<typeof useShipmentHeaderForm>;
  staged: ReturnType<typeof useStagedShipmentLines>;
}) {
  const alerts = useAlerts();
  const [save, { loading: saving }] = useMutation(SAVE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const {
    formState: { isDirty: headerDirty, dirtyFields },
    getValues,
    reset,
    issues: headerIssues,
  } = form;

  const handleSave = async () => {
    if (hasCriticalIssues(headerIssues)) {
      return;
    }
    const values = getValues();
    const linesInput = staged.toUpdateLinesInput();
    try {
      // One mutation, one transaction. Sending the header, container and pickup
      // changes separately meant a rule that rejected the second could leave the
      // first committed while the user was told the save failed.
      //
      // Only the sections the user touched are included — presence is what tells
      // the server which parts of the form to apply. In particular the pickup
      // section is sent only when its field is dirty, because a null date there
      // is a real instruction (clear the pickup and release its containers)
      // rather than "unchanged".
      await save({
        variables: {
          input: {
            id: shipment.id,
            ...(headerDirty
              ? {
                  header: toShipmentHeaderFields(values, Boolean(dirtyFields.plannedShipDate)),
                }
              : {}),
            ...(linesInput
              ? {
                  lines: {
                    addContainerIds: linesInput.addContainerIds,
                    removeLineIds: linesInput.removeLineIds,
                    setQuantities: linesInput.setQuantities,
                  },
                }
              : {}),
            ...(dirtyFields.scheduledPickupDate
              ? {
                  pickup: {
                    scheduledPickupDate: values.scheduledPickupDate
                      ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.scheduledPickupDate))
                      : null,
                  },
                }
              : {}),
          },
        },
      });
      staged.reset();
      // The header form keeps dirty values across a reset so the refetch can't
      // clobber someone mid-edit — which also means a successful save leaves it
      // looking unsaved. Rebase it on what was just persisted, or the page goes
      // on claiming changes and "Mark shipped" stays disabled until the user
      // discards an edit that already landed.
      reset(values);
      alerts.success("Shipment updated.");
    } catch (e) {
      // Nothing to unwind on the client: the save is one transaction, so a
      // failure means nothing persisted and every edit is still pending. The
      // form keeps its dirty state, which is now the truth rather than a guess.
      alerts.error(e instanceof Error ? e.message : "Failed to update the shipment.");
    }
  };

  // Throws away whatever is staged and leaves the shipment as it was saved.
  const handleDiscard = () => {
    reset();
    staged.reset();
  };

  return { handleSave, handleDiscard, saving };
}

/**
 * Abandoning the shipment.
 *
 * A draft never left the building, so it is deleted rather than left behind as
 * a canceled row; once requested, the vendor has its number and the record
 * stays.
 */
export function useAbandonShipment({
  shipment,
  isDraft,
  onDiscard,
  onDone,
  setAbandoning,
}: {
  shipment: Shipment;
  isDraft: boolean;
  onDiscard: () => void;
  onDone: () => void;
  setAbandoning: (abandoning: boolean) => void;
}) {
  const alerts = useAlerts();
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const [cancelShipment, { loading: canceling }] = useMutation(CANCEL_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [deleteShipment, { loading: deleting }] = useMutation(DELETE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });

  const handleAbandon = async () => {
    try {
      if (isDraft) {
        // Deleting the shipment navigates away, and the staged edits go with
        // it on purpose — the guard must not stop and ask about changes to a
        // record the user just chose to destroy.
        setAbandoning(true);
        await deleteShipment({ variables: { id: shipment.id } });
        alerts.success(`Shipment ${shipment.manifestRequestNumber} deleted.`);
        onDone();
        navigate(`/o/${tenantId}/waste/shipments`);
        return;
      }
      await cancelShipment({ variables: { id: shipment.id } });
      alerts.success(`Shipment ${shipment.manifestRequestNumber} canceled.`);
      // Canceling makes the shipment read-only, so the fields that produced any
      // pending edits unmount — but their dirty state would outlive them and
      // leave the guard asking about changes the user can no longer see, save,
      // or discard.
      onDiscard();
      onDone();
    } catch (e) {
      // The user is still here, and so are their staged edits, so the guard has
      // to come back — a delete that failed must not be the reason the next
      // navigation walks away from them without asking.
      setAbandoning(false);
      alerts.error(
        e instanceof Error ? e.message : `Failed to ${isDraft ? "delete" : "cancel"} the shipment.`
      );
    }
  };

  return { handleAbandon, abandoning: canceling || deleting };
}

/** The header form, owned by the page so one Save can cover the whole thing. */
export function useShipmentHeaderForm(shipment: Shipment) {
  const defaultValues = useMemo<EditShipmentFormValues>(
    () => ({
      vendorId: shipment.vendor?.id ?? null,
      transporterName: shipment.transporterName ?? "",
      destinationName: shipment.destinationName ?? "",
      destinationEpaId: shipment.destinationEpaId ?? "",
      plannedShipDate: storedDateToDateOnly(shipment.plannedShipDate),
      scheduledPickupDate: storedDateToDateOnly(shipment.scheduledPickupDate),
    }),
    [shipment]
  );

  return useValidatingForm<EditShipmentFormValues>(
    defaultValues,
    [],
    useShipmentUpdateValidation(shipment.id),
    [],
    { keepDirtyValuesOnReset: true }
  );
}

/**
 * Async header-edit validation; the seam future field checks land in. The
 * validation payload always includes the date (nothing persists from it, so
 * the dirty-gated timezone guard only matters on the real mutation input).
 */
function useShipmentUpdateValidation(shipmentId: string) {
  const [query] = useLazyQuery(VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: EditShipmentFormValues): Promise<Issue[]> => {
      const { data } = await query({
        variables: { input: toUpdateShipmentInput(shipmentId, values, true) },
      });
      return data?.wasteShipmentUpdateInputValidator ?? [];
    },
    [query, shipmentId]
  );
}

function toUpdateShipmentInput(
  shipmentId: string,
  values: EditShipmentFormValues,
  plannedShipDateDirty: boolean
) {
  return { id: shipmentId, ...toShipmentHeaderFields(values, plannedShipDateDirty) };
}

/**
 * The header fields alone, shared by the validator query (which wants them
 * under an id) and the combined Save's `header` section (which does not), so
 * the two can never normalize a value differently.
 */
export function toShipmentHeaderFields(
  values: EditShipmentFormValues,
  plannedShipDateDirty: boolean
) {
  return {
    vendorId: values.vendorId,
    transporterName: values.transporterName?.trim() || null,
    destinationName: values.destinationName?.trim() || null,
    destinationEpaId: values.destinationEpaId?.trim() || null,
    // Only re-normalize the date when the user actually touched it, so an
    // untouched value can't shift a day through the local timezone.
    ...(plannedShipDateDirty
      ? {
          plannedShipDate: values.plannedShipDate
            ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.plannedShipDate))
            : null,
        }
      : {}),
  };
}
